import { Component, OnInit, HostBinding } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material';
import { Observable, of, combineLatest, Subject } from 'rxjs';
import { finalize, first, map } from 'rxjs/operators';

import { Site } from '../shared/models/site';
import { Settings } from '../shared/models/settings';
import { Center } from '../shared/models/center';
import { SitesFilterService } from '../sites/sites-filter/sites-filter.service';
import { CenterService } from './center.service';
import { SettingsService } from './settings.service';
import { IncomeByItemReportService } from './income-by-item-report.service';
import { RoyaltyReportDataService } from './royalty-report-data.service';
import { ReportHelperService } from './reports/report-helper.service';

const locales = {
  'en-au': 'AUD',
  'en-ca': 'CAD',
  'en-gb': 'GBP',
  'en-nl': 'EUR',
  'en-sg': 'SGD',
  'en-us': 'USD',
  'en-za': 'ZAR',
  'fr-fr': 'EUR',
  'fr-ca': 'CAD',
  'pt-pt': 'EUR',
  'es-co': 'COP',
  'es-es': 'EUR',
  'tr-tr': 'TRY',
};

type SitesMeta = Record<string, { centers?: Center[]; settings?: Settings; error?: string; currency?: string }>;

@Component({
  selector: 'dw-royalty-report',
  templateUrl: './royalty-report.component.html',
  styleUrls: ['./royalty-report.component.scss'],
})
export class RoyaltyReportComponent implements OnInit {
  @HostBinding('class') public hostClass: string;

  private sites: Site[];
  public sitesMeta: SitesMeta;
  public sitesMeta$: Observable<SitesMeta>;
  public filteredSites$: Observable<Site[]>;
  public usSites$: Observable<Site[]>;
  public otherSites$: Observable<Site[]>;
  public date: string;
  public reportDate: string;
  public maxDate: string;
  public grandTotals: Record<string, any> = {};

  constructor(private router: Router, private route: ActivatedRoute, private matDialog: MatDialog,
              private sitesFilter: SitesFilterService, private center: CenterService, private settingsService: SettingsService,
              private incomeByItemReport: IncomeByItemReportService, private royaltyReportData: RoyaltyReportDataService,
              private reportHelper: ReportHelperService) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false; // force component re-instantiating on route param change
    this.hostClass = 'royalty-report';
    this.sites = route.snapshot.data.sites;
    let date = new Date();
    this.reportDate = date.toISOString().slice(0, 7); // date for report
    date.setUTCMonth(date.getUTCMonth() - 1);
    this.date = date.toISOString().slice(0, 7); // date to display
    this.maxDate = this.date;

    [this.sitesMeta, this.sitesMeta$] = watchObject({});
  }

  ngOnInit() {
    this.filteredSites$ = this.sitesFilter.predicate$
      .pipe(map(predicate => this.sitesFilter.filter(this.sites, predicate)));

    this.usSites$ = combineLatest([this.filteredSites$, this.sitesMeta$])
      .pipe(map(([sites, sitesMeta]) => sites.filter(site => (this.sitesMeta[site._id] || {}).currency === 'USD')));

    this.otherSites$ = combineLatest([this.filteredSites$, this.sitesMeta$])
      .pipe(map(([sites, sitesMeta]) => sites.filter(site => (sitesMeta[site._id] || {}).currency !== 'USD')));

    this.fetchReports();
  }

  public dateChanged(event) {
    let dt = new Date(this.date);
    dt.setUTCMonth(dt.getUTCMonth() + 1);
    this.reportDate = dt.toISOString().slice(0, 7);

    for (const meta of Object.values(this.sitesMeta)) {
      if (!meta.centers) { continue; }
      for (const center of meta.centers) {
        center.totals = null;
        center.error = null;
      }
    }
    this.fetchReports();
  }

  public pdf() {
    const report = this.royaltyReportData.dataToReport({ grandTotals: this.grandTotals, sites: this.sites, sitesMeta: this.sitesMeta });
    const doc = this.reportHelper.buildPdf({ report, config: { reportName: 'Royalty Report', date: this.date } });
    const blob = doc.output('blob');
    this.reportHelper.download({ blob, fileName: `royalty-report-${this.date}.pdf` });
  }

  public async csv() {
    const report = this.royaltyReportData.dataToReport({ grandTotals: this.grandTotals, sites: this.sites, sitesMeta: this.sitesMeta });
    const blob = await this.reportHelper.buildCsv({ report });
    this.reportHelper.download({ blob, fileName: `royalty-report-${this.date}.csv` });
  }

  private fetchReports() {
    for (const site of this.sites) {
      this.resetGrandTotals();

      const center$ = (this.sitesMeta[site._id] && this.sitesMeta[site._id].centers ? of(this.sitesMeta[site._id].centers) : this.center.query({ siteId: site._id }))
        .pipe(first());

      const setting$ = (this.sitesMeta[site._id] && this.sitesMeta[site._id].settings ? of(this.sitesMeta[site._id].settings) : this.settingsService.get({ siteId: site._id }))
        .pipe(first());

        combineLatest([center$, setting$])
          .subscribe(
            ([centers, settings]) => {
              this.sitesMeta[site._id] = {
                centers,
                settings: { isFranchise: settings.isFranchise, franchiseFees: settings.franchiseFees || [], locale: settings.locale },
                currency: locales[settings.locale] || 'USD',
              };

              for (const center of centers) {
                center.spin = true;
                this.incomeByItemReport.query({ siteId: site._id, centerId: center.id, date: this.reportDate, excludeSecurityDeposits: true })
                  .pipe(first())
                  .pipe(finalize(() => center.spin = false))
                  .subscribe(data => {
                    center.totals = this.royaltyReportData.calcTotals({ data, settings: this.sitesMeta[site._id].settings });
                    if (this.sitesMeta[site._id].currency === 'USD') {
                      this.addGrandTotals(center.totals);
                    }
                  }, err => { center.error = err.error.error || err.error.message || err.message || `${err.status} ${err.statusText}`; console.warn('ERR', JSON.stringify(err)); });
              }
            },
            err => {
              console.warn('ERR', JSON.stringify(err));
              this.sitesMeta[site._id] = { error: err.error.error || err.error.message || err.message || `${err.status} ${err.statusText}` }
            },
          );
    }
  }

  public isLoading() {
    for (const site of this.sites) {
      if (!this.sitesMeta[site._id]) { return true; }
      if (!this.sitesMeta[site._id].centers) { continue; }
      for (const center of this.sitesMeta[site._id].centers) {
        if (center.spin) { return true; }
      }
    }
  }

  private resetGrandTotals() {
    this.grandTotals.totalRevenue = 0;
    this.grandTotals.exemptRevenue = 0;
    this.grandTotals.royaltiesRevenue = 0;
    this.grandTotals.totalDue = 0;
    this.grandTotals.fees = [];
  }

  private addGrandTotals(totals: Record<string, any>) {
    this.grandTotals.totalRevenue += totals.totalRevenue;
    this.grandTotals.exemptRevenue += totals.exemptRevenue;
    this.grandTotals.royaltiesRevenue += totals.royaltiesRevenue;
    this.grandTotals.totalDue += totals.totalDue;

    for (const fee of totals.fees) {
      let grandFee = this.grandTotals.fees.find(f => f.name.toLowerCase() === fee.name.toLowerCase());
      if (!grandFee) {
        grandFee = { ...fee };
        this.grandTotals.fees.push(grandFee);
      } else {
        grandFee.amount += fee.amount;
      }
    }
  }

}

function watchObject(obj: SitesMeta): [SitesMeta, Observable<SitesMeta>] {
  const subject = new Subject<SitesMeta>();
  const proxy = new Proxy(obj, {
    set(target, prop: string, value) {
      target[prop] = value;
      subject.next(target);
      return true;
    }
  });
  return [proxy, subject.asObservable()];
}
